import { Images } from "@/constants/images"
import { DefaultPermissionStructure, HeroBannerButton, HeroBannerButtonStyles } from "@/types/types"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { ADMIN_API_CONFIG } from "../constants/api-configs"
import { DefaultImage } from "./external-components"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const replaceImageUrls = (content: string) => {
  const currentDomain = process.env.NEXT_PUBLIC_OLD_IMAGE_URL
  const newDomain = process.env.NEXT_PUBLIC_URL_IMAGES_PRODUCTION

  if (!currentDomain || !newDomain) {
    throw new Error("Environment variables CURRENT and DOMAIN must be set")
  }

  const regex = new RegExp(`${currentDomain.replace(/\/$/, "")}(/?)`, "g")

  const updatedContent = content.replace(regex, `${newDomain}/`)

  return updatedContent
}

export const dateFormat = (date: string, locale: string): string => {
  const parsedDate = new Date(date)

  if (isNaN(parsedDate.getTime())) {
    throw new Error("Invalid date format")
  }

  return parsedDate.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

export const formatDate = (createdAt: string | Date) => {
  const date = new Date(createdAt)
  const day = String(date.getUTCDate()).padStart(2, "0")
  const month = String(date.getUTCMonth() + 1).padStart(2, "0")
  const year = date.getUTCFullYear()
  const hours = String(date.getUTCHours()).padStart(2, "0")
  const minutes = String(date.getUTCMinutes()).padStart(2, "0")
  const seconds = String(date.getUTCSeconds()).padStart(2, "0")

  const formattedTime = `${hours}:${minutes}:${seconds}`
  const formattedDate = `${day}/${month}/${year}`
  return { formattedDate, formattedTime }
}

export function generateImagePath(
  inputPath: string | null | undefined,
  sizeType: "thumbnail" | "small" | "medium" | "large" | "original",
  formats?: {
    thumbnail?: { url?: string }
    small?: { url?: string }
    medium?: { url?: string }
    large?: { url?: string }
  } | null,
) {
  if (!inputPath) {
    return DefaultImage
  }

  const buildImageUrl = (url?: string | null) => {
    if (!url) return DefaultImage
    const normalizedUrl = String(url).trim()
    if (!normalizedUrl) return DefaultImage
    if (/^https?:\/\//i.test(normalizedUrl)) return normalizedUrl
    if (normalizedUrl.startsWith("//")) return `https:${normalizedUrl}`

    const base = (ADMIN_API_CONFIG.API_URL || "").replace(/\/$/, "")
    const path = normalizedUrl.startsWith("/") ? normalizedUrl : `/${normalizedUrl}`
    return `${base}${path}`
  }

  const allowedSizeTypes = ["thumbnail", "small", "medium", "large", "original"]

  if (sizeType === "original") {
    return buildImageUrl(inputPath)
  }

  if (!allowedSizeTypes.includes(sizeType)) {
    throw new Error(`Invalid size type. Allowed values are: ${allowedSizeTypes.join(", ")}`)
  }

  if (!formats) {
    return buildImageUrl(inputPath)
  }

  const formatOrder: Array<"thumbnail" | "small" | "medium" | "large"> = ["thumbnail", "small", "medium", "large"]
  const requestedIndex = formatOrder.indexOf(sizeType)

  if (requestedIndex >= 0 && formats[sizeType]?.url) {
    return buildImageUrl(formats[sizeType].url)
  }

  if (requestedIndex >= 0) {
    for (let i = requestedIndex + 1; i < formatOrder.length; i++) {
      const largerFormat = formatOrder[i]
      if (formats[largerFormat]?.url) {
        return buildImageUrl(formats[largerFormat].url)
      }
    }
  }

  return buildImageUrl(inputPath)
}
export const calcAvgStatsTour = (day?: number, distance?: number, percent?: boolean) =>
  percent ? ((distance ?? 0) / (day ?? 1) / (distance ?? 0)) * 100 : (distance ?? 0) / (day ?? 1)

export const removeAccents = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[âÂ]/g, "a")
    .replace(/[êÊ]/g, "e")
    .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
}

export const updateImageSrc = (src: string | undefined) => {
  if (!src || typeof src !== "string" || src?.trim() === "") return Images.imageEmpty.src

  return `${ADMIN_API_CONFIG.API_URL}${src}`
}

export const toImageSrc = (url?: string) => {
  if (!url) return DefaultImage
  const u = String(url).trim()
  if (!u) return DefaultImage
  if (/^https?:\/\//i.test(u)) return u
  if (u.startsWith("//")) return `https:${u}`
  // Local Next.js bundled assets (e.g. DefaultImage) must not be prefixed with the API host
  if (u.startsWith("/_next/") || u === DefaultImage) return u
  const base = (ADMIN_API_CONFIG.API_URL || "").replace(/\/$/, "")
  const path = u.startsWith("/") ? u : `/${u}`
  return `${base}${path}`
}

// "Vừa xong" cần bản dịch riêng: Intl với numeric:"auto" trả về "bây giờ"/"now",
// nghe cụt so với copy hiện tại của site.
const JUST_NOW: Record<string, string> = {
  vi: "Vừa xong",
  en: "Just now",
  cs: "Právě teď",
}

// locale mặc định "vi" để các caller cũ (admin, ui-builder) giữ nguyên hành vi.
export const formatTimeAgo = (iso?: string, locale: string = "vi"): string => {
  if (!iso) return ""
  const now = new Date()
  const then = new Date(iso)
  const diffMs = Math.max(0, now.getTime() - then.getTime())

  const lang = locale in JUST_NOW ? locale : "vi"
  // numeric:"always" giữ nguyên chuỗi vi hiện tại ("1 ngày trước", không phải "Hôm qua")
  // — formatTimestamp bên dưới đang match chính xác chuỗi đó. Với cs, Intl vẫn tự chia
  // số nhiều đúng ("před 1 dnem" vs "před 6 dny").
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "always" })

  const minutes = Math.floor(diffMs / (60 * 1000))
  if (minutes < 1) return JUST_NOW[lang]
  if (minutes < 60) return rtf.format(-minutes, "minute")

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return rtf.format(-hours, "hour")

  const days = Math.floor(hours / 24)
  if (days < 30) return rtf.format(-days, "day")

  const months = Math.floor(days / 30)
  if (months < 12) return rtf.format(-months, "month")

  const years = Math.floor(months / 12)
  return rtf.format(-years, "year")
}

export const cleanDescription = (description: string | undefined | null, maxLength: number = 150): string => {
  if (!description) return ""
  return description
    .replace(/<[^>]*>/g, "")
    .trim()
    .substring(0, maxLength)
}

export const formatDateSimple = (dateStr: string | undefined | null): string => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${day}-${month}-${year}`
}

export const formatDateSimpleYyyyMmDd = (dateStr: string | undefined | null): string => {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${year}-${month}-${day}`
}

export const formatTimestamp = (dateStr: string) => {
  const timeAgo = formatTimeAgo(dateStr)
  if (!timeAgo) return ""

  let formatted = timeAgo.toUpperCase()
  formatted = formatted.replace(/^1 NGÀY TRƯỚC$/, "MỘT NGÀY TRƯỚC")
  formatted = formatted.replace(/^1 GIỜ TRƯỚC$/, "MỘT GIỜ TRƯỚC")
  formatted = formatted.replace(/^1 PHÚT TRƯỚC$/, "MỘT PHÚT TRƯỚC")

  return formatted
}

/**
 * Tạo object buttonStyle từ button và buttonStyles
 * @param button - Button object từ slide
 * @param buttonStyles - Global button styles (optional)
 * @returns buttonStyle object với type any để linh động
 */
export const createButtonStyle = (button: HeroBannerButton, buttonStyles?: HeroBannerButtonStyles): any => {
  // Get button styles from individual button, fallback to buttonStyles
  const buttonSize = button.buttonSize || buttonStyles?.size
  const buttonFontSize = button.buttonFontSize || buttonStyles?.fontSize
  const buttonFontWeight = button.buttonFontWeight || buttonStyles?.fontWeight
  const buttonLineHeight = button.buttonLineHeight || buttonStyles?.lineHeight

  // Get hover styles from individual button
  const hoverBackgroundColor = button.hoverBackgroundColor
  const hoverColor = button.hoverColor
  const hoverBorder = button.hoverBorder
  const hoverTransform = button.hoverTransform
  const hoverOpacity = button.hoverOpacity
  const hoverBoxShadow = button.hoverBoxShadow

  // Parse buttonSize string to object
  let buttonWidth: string | undefined
  let buttonHeight: string | undefined
  if (buttonSize) {
    const widthMatch = buttonSize.match(/width:\s*([^;]+)/i)
    const heightMatch = buttonSize.match(/height:\s*([^;]+)/i)
    if (widthMatch) buttonWidth = widthMatch[1].trim()
    if (heightMatch) buttonHeight = heightMatch[1].trim()
  }
  const buttonSizeObj = buttonWidth || buttonHeight ? { width: buttonWidth, height: buttonHeight } : undefined

  return {
    buttonSize: buttonSizeObj,
    buttonFontSize,
    buttonFontWeight,
    buttonLineHeight,
    hoverBackgroundColor,
    hoverColor,
    hoverBorder,
    hoverTransform,
    hoverOpacity,
    hoverBoxShadow,
  }
}

// Re-export post navigation utilities for convenience
export { createPostClickHandler, handlePostClick, handlePostClickForLink, incrementPostViews } from "./post-navigation"

export const NOTIFICATION_CATEGORY_SLUGS = {
  vi: "thong-bao",
  en: "announcement",
  cs: "oznameni",
} as const

export type NotificationLocale = keyof typeof NOTIFICATION_CATEGORY_SLUGS

export const getNotificationSlug = (locale: string = "vi"): string => {
  const key = locale as NotificationLocale
  return NOTIFICATION_CATEGORY_SLUGS[key] ?? NOTIFICATION_CATEGORY_SLUGS.vi
}

export const DEFAULT_PERMISSIONS: DefaultPermissionStructure = {
  "plugin::users-permissions": {
    controllers: {
      auth: ["connect"],
      role: ["findOne"],
      user: ["findOne", "me"],
    },
  },
}

export const parseFolderIdFromPath = (path: string | null): number | null => {
  if (!path) return null
  const match = path.match(/^\/(\d+)$/)
  return match ? Number(match[1]) : null
}


